import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  UNIVERSITY_PROBATION_CREATE_EMAIL_PREVIEW_QUERY,
  type UniversityProbationCreateEmailPreview,
  type UniversityProbationCreateEmailPreviewQueryResponse,
  type UniversityProbationCreateEmailPreviewQueryVariables,
  type UniversityProbationEmailPreviewAudience
} from "../queries";

export type LoadUniversityProbationCreateEmailPreviewParams = {
  universityId: string;
  personIds: string[];
  noteTitle: string;
  noteContent: string;
  probationStartedAt?: string | null;
  audience: UniversityProbationEmailPreviewAudience;
  forUniversityStaff?: boolean;
};

export function useUniversityProbationCreateEmailPreview() {
  const [preview, setPreview] = useState<UniversityProbationCreateEmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    UniversityProbationCreateEmailPreviewQueryResponse,
    UniversityProbationCreateEmailPreviewQueryVariables
  >(UNIVERSITY_PROBATION_CREATE_EMAIL_PREVIEW_QUERY, {
    fetchPolicy: "network-only"
  });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async (params: LoadUniversityProbationCreateEmailPreviewParams) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({
        variables: {
          universityId: params.universityId,
          personIds: params.personIds,
          noteTitle: params.noteTitle,
          noteContent: params.noteContent,
          probationStartedAt: params.probationStartedAt,
          audience: params.audience,
          forUniversityStaff: params.audience === "STAFF_NOTIFICATION" ? (params.forUniversityStaff ?? true) : false
        }
      });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.universityProbationCreateEmailPreview ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [fetchPreview]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
